/**
 * Estado dos critérios adicionados pelo perito na mesa (E38.F3). // ux-neutrality:allow doc-comment
 *
 * Carrega os critérios custom da análise no mount/troca de tab e expõe // ux-neutrality:allow doc-comment
 * adicionar/remover. `add` espera o servidor confirmar (sem otimismo), com // ux-neutrality:allow doc-comment
 * `busy` para a UI desabilitar o botão durante o POST; `error` sinaliza a // ux-neutrality:allow doc-comment
 * última operação falha (limpo na próxima que conclui). // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api';
import {
  createCustomCriterion,
  deleteCustomCriterion,
  listCustomCriteria,
  type CustomCriterionCreateData,
  type CustomCriterionData,
} from '@/lib/custom_criteria';

export interface UseCustomCriteriaResult {
  criteria: CustomCriterionData[];
  /** POST de um critério em andamento (desabilita o botão de adicionar). */
  busy: boolean;
  /** Última operação de persistência falhou (limpo na próxima que conclui). */
  error: boolean;
  add: (input: CustomCriterionCreateData) => Promise<boolean>;
  remove: (id: string) => Promise<void>;
}

export function useCustomCriteria(caseId: string, analysisId: string): UseCustomCriteriaResult {
  const client = useApiClient();
  const [criteria, setCriteria] = useState<CustomCriterionData[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  // Troca de tab no meio de um request em voo: descarta a resposta antiga.
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    setCriteria([]);
    listCustomCriteria(client, caseId, analysisId)
      .then((records) => {
        if (generationRef.current === generation) setCriteria(records);
      })
      .catch(() => {
        // Falha de carga não bloqueia a mesa: a lista abre vazia e a próxima
        // operação reporta erro de persistência se persistir.
      });
  }, [client, caseId, analysisId]);

  const add = useCallback(
    async (input: CustomCriterionCreateData): Promise<boolean> => {
      setBusy(true);
      try {
        const record = await createCustomCriterion(client, caseId, analysisId, input);
        setError(false);
        setCriteria((current) => [...current, record]);
        return true;
      } catch {
        setError(true);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [client, caseId, analysisId],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      try {
        await deleteCustomCriterion(client, caseId, analysisId, id);
        setError(false);
        setCriteria((current) => current.filter((c) => c.id !== id));
      } catch {
        setError(true);
      }
    },
    [client, caseId, analysisId],
  );

  return { criteria, busy, error, add, remove };
}
